import { useState, useEffect, useCallback } from "react";
import { purchaseListService } from "@/services";
import { AvailableProduct } from "@/types/purchaseList.types";
import { useAuth } from "./useAuth";

interface StudentQRState {
  availableProducts: AvailableProduct[];
  allProducts: AvailableProduct[];
  selectedProduct: AvailableProduct | null;
  loading: boolean;
  error: string | null;
}

interface StudentQRStats {
  totalProducts: number;
  availableProducts: number;
  deliveredProducts: number;
  pendingProducts: number;
  activeQRCodes: number;
}

interface UseStudentQRReturn {
  // Estado
  availableProducts: AvailableProduct[];
  allProducts: AvailableProduct[];
  selectedProduct: AvailableProduct | null;
  loading: boolean;
  error: string | null;
  stats: StudentQRStats & { allProducts: AvailableProduct[] };

  // Acciones
  loadAvailableProducts: () => Promise<void>;
  selectProduct: (product: AvailableProduct) => void;
  clearSelection: () => void;
  clearError: () => void;

  // Utilidades
  isStudent: boolean;
  hasProducts: boolean;
  hasSelectedProduct: boolean;
  getProductQRCode: (product: AvailableProduct) => string | null;
  getProductStatus: (
    product: AvailableProduct
  ) => "available" | "delivered" | "pending";
}

const initialState: StudentQRState = {
  availableProducts: [],
  allProducts: [],
  selectedProduct: null,
  loading: false,
  error: null,
};

export const useStudentQR = (): UseStudentQRReturn => {
  const [state, setState] = useState<StudentQRState>(initialState);
  const { user } = useAuth();

  // Cargar productos disponibles para el estudiante
  const loadAvailableProducts = useCallback(async () => {
    if (!user || !user.roles?.includes("Estudiante")) return;

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      // Obtener todos los productos del estudiante para estadísticas
      const allProductsResponse = await purchaseListService.getStudentAllProducts(
        user.id
      );
      
      // Obtener productos disponibles para canje
      const availableProductsResponse = await purchaseListService.getStudentAvailableProducts(
        user.id
      );

      setState((prev) => ({
        ...prev,
        allProducts: allProductsResponse.allProducts || [],
        availableProducts: availableProductsResponse.availableProducts || [],
        loading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error:
          error instanceof Error
            ? error.message
            : "Error cargando productos disponibles",
        loading: false,
      }));
    }
  }, [user]);

  // Seleccionar producto
  const selectProduct = useCallback((product: AvailableProduct) => {
    setState((prev) => ({ ...prev, selectedProduct: product }));
  }, []);

  // Limpiar selección
  const clearSelection = useCallback(() => {
    setState((prev) => ({ ...prev, selectedProduct: null }));
  }, []);

  // Limpiar error
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  // Obtener código QR de un producto
  const getProductQRCode = useCallback(
    (product: AvailableProduct): string | null => {
      return product.qrCode;
    },
    []
  );

  // Obtener estado de un producto
  const getProductStatus = useCallback(
    (product: AvailableProduct): "available" | "delivered" | "pending" => {
      if (product.deliveredQuantity >= product.assignedQuantity) {
        return "delivered";
      }
      if (product.deliveredQuantity > 0) {
        return "pending";
      }
      return "available";
    },
    []
  );

  // Calcular estadísticas usando la respuesta del backend
  const calculateStats = useCallback((): StudentQRStats => {
    // Usar las estadísticas calculadas en el backend
    const allProductsResponse = state.allProducts;
    
    if (allProductsResponse.length === 0) {
      return {
        totalProducts: 0,
        availableProducts: 0,
        deliveredProducts: 0,
        pendingProducts: 0,
        activeQRCodes: 0,
      };
    }

    // Contar productos por estado
    const totalProducts = allProductsResponse.length;
    const availableProducts = allProductsResponse.filter(p => p.remainingQuantity > 0).length;
    const deliveredProducts = allProductsResponse.filter(p => p.deliveredQuantity > 0).length;
    const pendingProducts = allProductsResponse.filter(p => 
      p.deliveredQuantity > 0 && p.remainingQuantity > 0
    ).length;
    const activeQRCodes = allProductsResponse.filter(p => p.qrCode).length;

    return {
      totalProducts,
      availableProducts,
      deliveredProducts,
      pendingProducts,
      activeQRCodes,
    };
  }, [state.allProducts]);

  // Cargar datos iniciales
  useEffect(() => {
    if (user && user.roles?.includes("Estudiante")) {
      loadAvailableProducts();
    }
  }, [user, loadAvailableProducts]);

  // Actualizar estadísticas cuando cambien los productos
  const stats = calculateStats();

  return {
    // Estado
    availableProducts: state.availableProducts,
    allProducts: state.allProducts,
    selectedProduct: state.selectedProduct,
    loading: state.loading,
    error: state.error,
    stats: {
      ...stats,
      allProducts: state.allProducts,
    },

    // Acciones
    loadAvailableProducts,
    selectProduct,
    clearSelection,
    clearError,

    // Utilidades
    isStudent: user?.roles?.includes("Estudiante") || false,
    hasProducts: state.availableProducts.length > 0,
    hasSelectedProduct: state.selectedProduct !== null,
    getProductQRCode,
    getProductStatus,
  };
};
